import { lazy, Suspense, type ComponentType } from "react";

/**
 * The Toaster, off the first load.
 *
 * App mounts the Toaster on every screen, so a static import put Radix Toast
 * and the toast rendering (~10 KB gzip) in the entry chunk — bytes no first
 * paint draws, since nothing has been said yet. The chunk is requested the
 * moment this module is evaluated, alongside the rest of the app rather than
 * in front of it, so it has normally arrived by the first paint.
 *
 * Nothing said meanwhile is lost: use-toast keeps its queue in module state
 * and the Toaster re-reads it when it subscribes (see the note in
 * hooks/use-toast.ts), so a toast fired before this resolves is shown when it
 * does. The confirm outlet lives in the Toaster too; a confirm asked in that
 * first instant falls back to window.confirm() (lib/confirm.ts) rather than
 * hanging.
 */
// A chunk that fails to load (a deploy mid-session) costs the notices, never
// the screen: this sits outside any page's error boundary.
const toasterChunk: Promise<{ default: ComponentType }> = import("@/components/ui/toaster").then(
  (m) => ({ default: m.Toaster }),
  () => ({ default: () => null }),
);
const LazyToaster = lazy(() => toasterChunk);

export function Toaster() {
  return (
    <Suspense fallback={null}>
      <LazyToaster />
    </Suspense>
  );
}
